import React, { useState, useEffect, useContext } from 'react'
import { Navigate } from 'react-router-dom'
import axios from 'axios'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { Modal, Button, Form } from 'react-bootstrap'
import { UserContext } from '../context'
import { getAccessToken } from '../services/AuthService'
import { showErrorNotification } from '../services/notificationService'
import { Formik, FieldArray } from 'formik'
import './SubscriptionPlansAdmin.css'

const money = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(
    Number(n || 0)
  )

/* ---- helpers para metadata (pares clave/valor) ---- */
const pairsFromObj = (obj) =>
  Object.entries(obj || {}).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value, null, 0),
  }))

const tryParse = (text) => {
  if (typeof text !== 'string') return text
  const t = text.trim()
  const looksJson =
    (t.startsWith('{') && t.endsWith('}')) ||
    (t.startsWith('[') && t.endsWith(']'))
  const looksBool = t === 'true' || t === 'false'
  const looksNum = !Number.isNaN(Number(t)) && t !== ''
  if (looksJson || looksBool || looksNum) {
    try { return JSON.parse(t) } catch { 
      return text
    }
  }
  return text
}

const objFromPairs = (pairs) =>
  (pairs || []).reduce((acc, row) => {
    const key = (row.key || '').trim()
    if (!key) return acc
    acc[key] = tryParse(row.value)
    return acc
  }, {})

/* ---- validaciones ---- */
// put this where your schema lives
const makeValidationSchema = (mustProvideImage) =>
  Yup.object({
    name: Yup.string().required('Nombre requerido'),
    description: Yup.string().required('Descripción requerida'),
    price: Yup.number().required('Precio requerido').min(0),
    frequency_type: Yup.string().oneOf(['month', 'year']).required(),
    features: Yup.array()
      .of(Yup.object({ name: Yup.string().required('Requerido') }))
      .min(1, 'Agrega al menos una característica'),
    metadataPairs: Yup.array().of(
      Yup.object({ key: Yup.string().nullable(), value: Yup.string().nullable() })
    ),

    // Image rules
    image: mustProvideImage
      ? Yup.mixed()
        .required('Imagen requerida')
        .test('fileType', 'Formato no soportado (usa JPG, PNG, WEBP )', (v) =>
          !!v &&
          ['image/jpeg', 'image/png', 'image/webp'].includes(v.type)
        )
        .test('fileSize', 'La imagen es muy grande (máx. 5MB)', (v) => !!v && v.size <= 5 * 1024 * 1024)
      : Yup.mixed()
        .nullable()
        .test('fileType', 'Formato no soportado (usa JPG, PNG, WEBP)', (v) =>
          !v || ['image/jpeg', 'image/png', 'image/webp'].includes(v.type)
        )
        .test('fileSize', 'La imagen es muy grande (máx. 5MB)', (v) => !v || v.size <= 5 * 1024 * 1024),
  })

export default function SubscriptionPlansAdmin({ isLoggedIn }) {
  const { user } = useContext(UserContext)
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)

  // modal
  const [showModal, setShowModal] = useState(false)
  const [currentPlan, setCurrentPlan] = useState(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState('')

  useEffect(() => {
     (async () => {
      try {
        const token = getAccessToken()
        const headers = { Authorization: `Bearer ${token}` }
        const { data } = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/subscription_plan/`,
          { headers, timeout: 5000 }
        )
        setPlans(data || [])
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const openModal = (plan = null) => {
    setCurrentPlan(plan)
    setImagePreviewUrl(plan?.image || '')
    setShowModal(true)
  }
  const closeModal = () => {
    setShowModal(false)
    setCurrentPlan(null)
    setImagePreviewUrl('')
  }

  const handleImageChange = (e, setFieldValue) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onloadend = () => setImagePreviewUrl(reader.result)
    reader.readAsDataURL(file)
    setFieldValue('image', file)
  }

  const submitPlan = async (values, { setSubmitting, setFieldError }) => {
    const payload = { ...values, metadata: objFromPairs(values.metadataPairs) }

    const requireImage = !currentPlan || !currentPlan.image;

    if (requireImage && !values.image) {
      setFieldError('image', 'Imagen requerida');
      toast.error('Por favor agrega una imagen de portada.');
      setSubmitting(false);
      return; // block submit
    }

    const formData = new FormData()
    Object.entries(payload).forEach(([k, v]) => {
      if (k === 'features' || k === 'metadata') {
        formData.append(k, JSON.stringify(v ?? {}))
      } else if (k === 'image' && v) {
        formData.append('image', v)
      } else if (k !== 'metadataPairs') {
        formData.append(k, String(v ?? ''))
      }
    })

    try {
      const token = getAccessToken()
      const headers = { Authorization: `Bearer ${token}` }
      if (currentPlan) {
        await axios.put(
          `${process.env.REACT_APP_BASE_URL}/api/subscription_plan/${currentPlan.id}/`,
          formData,
          { headers, timeout: 5000 }
        )
        toast.success('Plan actualizado')
      } else {
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/api/subscription_plan/`,
          formData,
          { headers, timeout: 5000 }
        )
        toast.success('Plan creado')
      }
      const { data } = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/subscription_plan/`,
        { headers, timeout: 5000 }
      )
      setPlans(data || [])
      closeModal()
    } catch (e) {
      showErrorNotification(e)
    } finally {
      setSubmitting(false)
    }
  }

  const deletePlan = async (id) => {
    if (!window.confirm('¿Eliminar este plan?')) return
    try {
      const token = getAccessToken()
      const headers = { Authorization: `Bearer ${token}` }
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/subscription_plan/${id}/`,
        { headers, timeout: 5000 }
      )
      setPlans((p) => p.filter((x) => x.id !== id))
      toast.success('Plan eliminado')
    } catch (e) {
      showErrorNotification(e)
    }
  }

  if (!isLoggedIn || (user && !user.is_staff)) return <Navigate to="/log-in" />
  if (loading) return <div className="spa__loading">Cargando…</div>
  const mustProvideImage = !currentPlan || !currentPlan.image;

  return (
    <div className="spa">
      <header className="spa__header">
        <h1>Planes de suscripción</h1>
        <p>Administra los planes que ofreces: precio, periodo, beneficios e imagen.</p>
        <button className="btn btn--primary" onClick={() => openModal()}>
          + Nuevo plan
        </button>
      </header>

      <section className="spa__list">
        {plans.map((plan) => (
          <article className="spa__card" key={plan.id}>
            <div className="spa__media">
              {plan.image && <img src={plan.image} alt={plan.name} loading="lazy" />}
              <div className="spa__veil" />
              <div className="spa__price">
                {money(plan.price)} / {plan.frequency_type === 'year' ? 'año' : 'mes'}
              </div>

              <div className="spa__overlay">
                <h3 className="spa__title">{plan.name}</h3>
                {plan.description && <p className="spa__subtitle">{plan.description}</p>}
                {Array.isArray(plan.features) && plan.features.length > 0 && (
                  <ul className="spa__features">
                    {plan.features.map((f, i) => <li key={i}>{f.name}</li>)}
                  </ul>
                )}
                <div className="spa__actions">
                  <button className="btn btn--light" onClick={() => openModal(plan)}>Editar</button>
                  <button className="btn btn--danger" onClick={() => deletePlan(plan.id)}>Eliminar</button>
                </div>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* Modal: we control layout; do NOT pass centered/scrollable */}
      <Modal
        show={showModal}
        onHide={closeModal}
        size="lg"
        className="spa__modal"
        dialogClassName="spa__dialog"
        contentClassName="spa__content"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>{currentPlan ? 'Editar plan' : 'Nuevo plan'}</Modal.Title>
        </Modal.Header>

        <Formik
          enableReinitialize
          validationSchema={makeValidationSchema(mustProvideImage)}
          initialValues={{
            name: currentPlan?.name || '',
            description: currentPlan?.description || '',
            price: currentPlan?.price ?? '',
            frequency_type: currentPlan?.frequency_type || 'month',
            features: currentPlan?.features || [
              { name: 'Contenido de yoga actualizado' },
              { name: 'Videos ilimitados' },
              { name: 'Acceso a 1 clase presencial al mes *Si radicas en Guadalajara, Jalisco, México' },
              { name: 'Cancela cuando quieras' },
            ],
            metadataPairs: pairsFromObj(currentPlan?.metadata || { buttonText: 'Subscríbete al plan' }),
            image: null,
          }}
          onSubmit={(values, { setSubmitting, setFieldError }) =>
            submitPlan(values, { setSubmitting, setFieldError })
          }
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>
              {/* INLINE STYLE is the final override that wins in hostile CSS envs */}
              <Modal.Body
                className="spa__body"
                style={{ maxHeight: 'calc(100svh - 180px)', overflowY: 'auto' }}
              >
                <div className="spa__form-grid">
                  <div className="spa__field">
                    <Form.Label>Nombre</Form.Label>
                    <Form.Control
                      type="text" name="name" value={values.name}
                      onChange={handleChange} onBlur={handleBlur}
                      isInvalid={touched.name && !!errors.name}
                    />
                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                  </div>

                  <div className="spa__field">
                    <Form.Label>Precio</Form.Label>
                    <Form.Control
                      type="number" name="price" value={values.price}
                      onChange={handleChange} onBlur={handleBlur}
                      isInvalid={touched.price && !!errors.price}
                    />
                    <Form.Control.Feedback type="invalid">{errors.price}</Form.Control.Feedback>
                  </div>

                  <div className="spa__field">
                    <Form.Label>Frecuencia</Form.Label>
                    <Form.Select
                      name="frequency_type" value={values.frequency_type}
                      onChange={handleChange} onBlur={handleBlur}
                      isInvalid={touched.frequency_type && !!errors.frequency_type}
                    >
                      <option value="month">Mensual</option>
                      <option value="year">Anual</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.frequency_type}</Form.Control.Feedback>
                  </div>

                  <div className="spa__field spa__image">
                    <Form.Label>Imagen (portada)</Form.Label>
                    {imagePreviewUrl && <img src={imagePreviewUrl} alt="Preview" className="spa__img" />}
                    <Form.Control
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, setFieldValue)}
                      isInvalid={!!errors.image}   // optional, for Bootstrap styling
                    />
                    {errors.image && <div className="invalid-feedback d-block">{errors.image}</div>}
                  </div>

                  <div className="spa__field spa__description">
                    <Form.Label>Descripción</Form.Label>
                    <Form.Control
                      as="textarea" rows={4} name="description" value={values.description}
                      onChange={handleChange} onBlur={handleBlur}
                      isInvalid={touched.description && !!errors.description}
                    />
                    <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                  </div>

                  {/* Características */}
                  <div className="spa__field spa__list">
                    <Form.Label>Características</Form.Label>
                    <FieldArray name="features">
                      {({ remove, push }) => (
                        <>
                          {values.features.map((f, i) => (
                            <div className="spa__row" key={i}>
                              <Form.Control
                                type="text"
                                placeholder="Ej. Acceso ilimitado a videos"
                                value={f.name}
                                onChange={(e) => setFieldValue(`features.${i}.name`, e.target.value)}
                                isInvalid={Array.isArray(errors.features) && !!errors.features[i]?.name}
                              />
                              <button type="button" className="btn btn--ghost" onClick={() => remove(i)} aria-label="Eliminar">✕</button>
                            </div>
                          ))}
                          <button type="button" className="btn btn--light spa__add" onClick={() => push({ name: '' })}>
                            + Añadir característica
                          </button>
                          {typeof errors.features === 'string' && (
                            <div className="invalid-feedback d-block">{errors.features}</div>
                          )}
                        </>
                      )}
                    </FieldArray>
                  </div>

                  {/* Metadata */}
                  <div className="spa__field spa__list">
                    <Form.Label>Metadata (opcional)</Form.Label>
                    <FieldArray name="metadataPairs">
                      {({ remove, push }) => (
                        <>
                          {values.metadataPairs.map((row, i) => (
                            <div className="spa__row" key={i}>
                              <Form.Control
                                placeholder="clave (p.ej. buttonText)" value={row.key}
                                onChange={(e) => setFieldValue(`metadataPairs.${i}.key`, e.target.value)}
                              />
                              <Form.Control
                                placeholder="valor (texto, número, true/false o JSON)" value={row.value}
                                onChange={(e) => setFieldValue(`metadataPairs.${i}.value`, e.target.value)}
                              />
                              <button type="button" className="btn btn--ghost" onClick={() => remove(i)} aria-label="Eliminar">✕</button>
                            </div>
                          ))}
                          <button type="button" className="btn btn--light spa__add" onClick={() => push({ key: '', value: '' })}>
                            + Añadir par clave/valor
                          </button>
                        </>
                      )}
                    </FieldArray>
                  </div>
                </div>
              </Modal.Body>

              <Modal.Footer className="spa__footer">
                <Button variant="outline-secondary" onClick={closeModal}>Cancelar</Button>
                <Button type="submit" className="btn btn--primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Guardando…' : 'Guardar plan'}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  )
}
